// =============================================================================
// Origin UI — src/pages/compliance/DeclarationForm.tsx
// CM-9 — ad-hoc declaration flow (Gift / Conflict of Interest).
// Reuses the config-driven FormRenderer with no gating, no video, no document.
// Declarations resolve their target version server-side (item.CurrentVersionId),
// so no assignment is required. Never appears in Needs Attention.
// =============================================================================

import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { complianceApi } from '@/services/complianceApi';
import type {
  ComplianceItemDetail,
  ComplianceFormField,
} from '@/services/complianceApi';
import {
  FormRenderer,
  validateAnswers,
  collectAnswers,
} from '@/components/compliance/FormRenderer';
import type { AnswerMap } from '@/components/compliance/FormRenderer';
import styles from './AttestationFlow.module.css';

export function DeclarationForm() {
  const { itemId } = useParams<{ itemId: string }>();
  const [searchParams] = useSearchParams();
  const prefill = searchParams.get('prefill') === 'true';
  const navigate = useNavigate();

  const [item, setItem] = useState<ComplianceItemDetail | null>(null);
  const [fields, setFields] = useState<ComplianceFormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [answers, setAnswers] = useState<AnswerMap>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!itemId) return;
    let cancelled = false;

    async function load() {
      try {
        const detail = await complianceApi.getItem(itemId!);
        if (cancelled) return;
        setItem(detail);

        let parsedFields: ComplianceFormField[] = [];
        if (detail.formSchema) {
          try {
            const parsed = JSON.parse(detail.formSchema) as ComplianceFormField[];
            parsedFields = Array.isArray(parsed) ? parsed : [];
            setFields(parsedFields);
          } catch {
            setFields([]);
            setError('This form could not be loaded. Please contact Compliance.');
            return;
          }
        }

        // Prefill from latest submission if ?prefill=true
        if (prefill && parsedFields.length > 0) {
          try {
            const subs = await complianceApi.getMySubmissions();
            const latest = subs
              .filter((s) => s.complianceItemId === itemId)
              .sort((a, b) => new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime())[0];
            if (latest && !cancelled) {
              const detail2 = await complianceApi.getSubmissionDetail(latest.complianceSubmissionId);
              if (detail2?.answers && !cancelled) {
                try {
                  const prevAnswers = JSON.parse(detail2.answers) as Record<string, unknown>;
                  const mapped: AnswerMap = {};
                  for (const f of parsedFields) {
                    if (prevAnswers[f.key] !== undefined) {
                      mapped[f.key] = prevAnswers[f.key] as AnswerMap[string];
                    }
                  }
                  setAnswers(mapped);
                } catch {
                  // Ignore parse errors — start with blank form
                }
              }
            }
          } catch {
            // Prefill is best-effort — don't block the form
          }
        }
      } catch (err) {
        console.warn('Failed to load declaration item', err);
        if (!cancelled) setError('Could not load this declaration.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [itemId, prefill]);

  async function handleSubmit() {
    if (!item) return;
    const errs = validateAnswers(fields, answers);
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    setSubmitError(null);
    try {
      await complianceApi.submit({
        complianceItemId: item.complianceItemId,
        answers: collectAnswers(fields, answers) as Record<string, unknown>,
      });
      setSubmitted(true);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Submission could not be completed.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading&hellip;</div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <button className={styles.backLink} onClick={() => navigate('/compliance')}>
            ‹ Back to Compliance
          </button>
          <h1 className={styles.title}>Compliance</h1>
        </header>
        <div className={styles.card}>
          <div className={styles.error}>{error ?? 'Declaration not found.'}</div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={styles.page}>
        <header className={styles.header}>
          <h1 className={styles.title}>{item.title}</h1>
        </header>
        <div className={styles.card}>
          <div className={styles.successPanel}>
            <div className={styles.successTick}>✓</div>
            <h2 className={styles.successTitle}>Declaration submitted</h2>
            <p className={styles.successText}>
              Thank you. Your declaration has been submitted to Compliance.
            </p>
            <button
              className={styles.primaryBtn}
              onClick={() => navigate('/compliance')}
            >
              Back to Compliance
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button className={styles.backLink} onClick={() => navigate('/compliance')}>
          ‹ Back to Compliance
        </button>
        <h1 className={styles.title}>{item.title}</h1>
        <p className={styles.subtitle}>
          Complete and submit this declaration. Your submission goes to the
          Compliance team.
        </p>
      </header>

      <div className={styles.card}>
        {fields.length === 0 ? (
          <div className={styles.error}>This declaration has no form configured.</div>
        ) : (
          <FormRenderer
            fields={fields}
            values={answers}
            onChange={setAnswers}
            errors={formErrors}
            disabled={submitting}
          />
        )}
      </div>

      <div className={styles.submitBar}>
        {submitError && <p className={styles.error}>{submitError}</p>}
        <button
          className={styles.primaryBtn}
          onClick={handleSubmit}
          disabled={submitting || fields.length === 0}
        >
          {submitting ? 'Submitting…' : 'Submit declaration'}
        </button>
      </div>
    </div>
  );
}
